import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { toastUtils } from '@/common/utils/toastUtils';
import { useAuthStore } from '@/store/useAuthStore';
import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { spaceService } from '@/services/spaceService';
import { authService } from '@/services/authService';
import { companyUserService, CompanyUserDto, ApplyCompanyUserDto } from '@/services/companyUserService';
import { useNavigationStore } from '@/common/store';
import config from '@/config';
import { LoginCompany, ServiceType } from '@/common/types/login';
import Avatar from '@/common/components/avatar/Avatar';
import { Building2, Rocket, Crown, UserCheck, CreditCard, Settings, Zap, TrendingUp, Lightbulb } from 'lucide-react';
import { billingService } from '@/services/billingService';
import { useBillingStore } from '@/store/useBillingStore';
import { companyService, MasterCompanyDto } from '@/services/companyService';

// Types
import { TabType } from './types';

// Components
import SpaceTab from './components/space/SpaceTab';
import MasterTab from './components/master/MasterTab';
import AddMasterModal from './components/master/AddMasterModal';
import ApplicationTab from './components/application/ApplicationTab';
import BillingTab from './components/billing/BillingTab';
import PlanSelectionModal from './components/billing/PlanSelectionModal';

// Styles
import { 
  fadeInUp, fadeIn, EmptyState, EmptyIconWrapper, EmptyTitle, EmptyDescription 
} from './SharedStyles';

const CompanySpaceManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyCode } = useParams<{ companyCode: string }>();
  const { setHeaderTitle, clearBackNavigation, setBackNavigation } = useNavigationStore();
  const [isLoading, setIsLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUserDto[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [masterActionLoading, setMasterActionLoading] = useState<number | null>(null);
  const [showAddMasterPanel, setShowAddMasterPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 모달 상태 (스페이스 추가 시 플랜/결제)
  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false);
  const [pendingSpaceType, setPendingSpaceType] = useState<string | null>(null);
  const cardPopupRef = useRef<Window | null>(null);

  const { loadBillingData } = useBillingStore();
  
  // 가입 신청 관련 상태
  const [applications, setApplications] = useState<ApplyCompanyUserDto[]>([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  
  const { user } = useAuthStore();

  const [masterCompany, setMasterCompany] = useState<MasterCompanyDto | null>(null);
  const [isFetchingCompany, setIsFetchingCompany] = useState(false);

  const refreshCompanyData = useCallback(async () => {
    if (!companyCode) return;
    try {
      const companies = await companyService.getMasterCompanies();
      const found = companies.find((c) => c.code === companyCode);
      if (found) setMasterCompany(found);
    } catch {
      // silent
    }
  }, [companyCode]);

  useEffect(() => {
    if (companyCode && !masterCompany && !isFetchingCompany) {
      setIsFetchingCompany(true);
      refreshCompanyData().finally(() => setIsFetchingCompany(false));
    }
  }, [companyCode, masterCompany, isFetchingCompany, refreshCompanyData]);

  const company: LoginCompany | undefined = useMemo(() => {
    if (!masterCompany) return undefined;

    return {
      id: masterCompany.id,
      name: masterCompany.name,
      businessNumber: masterCompany.businessNumber,
      currency: masterCompany.currency,
      code: masterCompany.code,
      services: masterCompany.service.map(s => ({ type: s as ServiceType, isAvailable: true, isNotice: false })),
      logo: masterCompany.logo || '',
    };
  }, [masterCompany]);

  const validTabs: TabType[] = ['space', 'master', 'application', 'billing'];
  const basePath = `/space-management/company/${companyCode}`;
  const lastSegment = location.pathname.split('/').pop() || '';
  const activeTab: TabType = validTabs.includes(lastSegment as TabType) ? (lastSegment as TabType) : 'space';

  // 가입 신청 필터링 (대기중인 신청만)
  const pendingApplications = useMemo(() => applications.filter(app => app.isAccepted === undefined || app.isAccepted === null), [applications]);
  const processedApplications = useMemo(() => applications.filter(app => app.isAccepted !== undefined && app.isAccepted !== null), [applications]);
  
  // 마스터와 일반 멤버 분리
  const masterUsers = useMemo(() => companyUsers.filter(u => u.isMaster), [companyUsers]);
  const nonMasterUsers = useMemo(() => companyUsers.filter(u => !u.isMaster), [companyUsers]);
  
  // 검색 필터링
  const filteredNonMasterUsers = useMemo(() => nonMasterUsers.filter(u => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      (u.name && u.name.toLowerCase().includes(query)) ||
      (u.email && u.email.toLowerCase().includes(query)) ||
      (u.position && u.position.toLowerCase().includes(query))
    );
  }), [nonMasterUsers, searchQuery]);

  // 회사 사용자 목록 로드
  const loadCompanyUsers = useCallback(async () => {
    if (!company) return;
    setIsLoadingUsers(true);
    try {
      const users = await companyUserService.getCompanyUserList(company.id);
      setCompanyUsers(users);
    } catch (error) {
      toastUtils.error('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingUsers(false);
    }
  }, [company]);

  // 가입 신청 목록 로드
  const loadApplications = useCallback(async () => {
    if (!company) return;
    setIsLoadingApplications(true);
    try {
      const apps = await companyUserService.getApplyCompanyUsers(company.id);
      setApplications(apps);
    } catch (error) {
      toastUtils.error('가입 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingApplications(false);
    }
  }, [company]);

  useEffect(() => {
    if (company) {
      setHeaderTitle(`${company.name} 관리`);
      clearBackNavigation();
      setBackNavigation('회사 관리', '/space-management');
      loadCompanyUsers();
    }
  }, [company, setHeaderTitle, clearBackNavigation, setBackNavigation, loadCompanyUsers]);

  useEffect(() => {
    if (activeTab === 'application' && company) {
      loadApplications();
    }
  }, [activeTab, company, loadApplications]);

  const handleCardRegisterMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data?.type?.startsWith('CARD_REGISTER_')) return;
      if (!company) return;

      cardPopupRef.current = null;

      if (event.data.type === 'CARD_REGISTER_SUCCESS') {
        toastUtils.success('결제 수단이 등록되었습니다.');
        await loadBillingData(company.id, 'INVESTOR');
      } else if (event.data.type === 'CARD_REGISTER_FAIL') {
        toastUtils.error(event.data.error || '카드 등록에 실패했습니다.');
      } else if (event.data.type === 'CARD_REGISTER_CANCEL') {
        toastUtils.info('카드 등록이 취소되었습니다.');
      }
    },
    [company, loadBillingData],
  );

  useEffect(() => {
    window.addEventListener('message', handleCardRegisterMessage);
    return () => window.removeEventListener('message', handleCardRegisterMessage);
  }, [handleCardRegisterMessage]);

  if (isFetchingCompany) {
    return (
      <PageContainer>
        <EmptyState>
          <EmptyIconWrapper>
            <Building2 size={48} />
          </EmptyIconWrapper>
          <EmptyTitle>회사 정보를 불러오는 중...</EmptyTitle>
        </EmptyState>
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <EmptyState>
          <EmptyIconWrapper>
            <Building2 size={48} />
          </EmptyIconWrapper>
          <EmptyTitle>회사 정보를 찾을 수 없습니다</EmptyTitle>
          <EmptyDescription>올바른 경로로 접근해주세요.</EmptyDescription>
        </EmptyState>
      </PageContainer>
    );
  }

  // 스페이스 관련 파생 상태 (SpaceTab에 필요하지만 여기서도 쓸 수 있도록 정의)
  const allSpaces = [
    {
      type: 'STARTUP',
      name: '스타트업',
      icon: <Rocket size={24} />,
      description: '스타트업을 위한 대시보드와 서비스',
      isActive: company.services.some(s => s.type === 'STARTUP'),
      canCreate: !company.services.some(s => s.type === 'STARTUP'),
    },
    {
      type: 'INVESTOR',
      name: '투자사',
      icon: <TrendingUp size={24} />,
      description: '투자 관리 및 딜 소싱',
      isActive: company.services.some(s => s.type === 'INVESTOR'),
      canCreate: !company.services.some(s => s.type === 'INVESTOR'),
    },
    {
      type: 'INNOVATE',
      name: '선발 프로그램',
      icon: <Lightbulb size={24} />,
      description: '프로그램 운영 및 관리',
      isActive: company.services.some(s => s.type === 'INNOVATE'),
      canCreate: !company.services.some(s => s.type === 'INNOVATE'),
      isPreparing: true,
    },
  ];

  const activeSpaces = allSpaces.filter(space => space.isActive);
  const availableSpaces = allSpaces.filter(space => space.canCreate);

  // 스페이스 생성 핸들러
  const handleCreateSpace = async (spaceType: string) => {
    const emailVerified = user?.emailVerified;
    if (!emailVerified) {
      toastUtils.error('이메일 인증을 먼저 해주세요.');
      return;
    }
    
    if (spaceType === 'INVESTOR') {
      setPendingSpaceType(spaceType);
      await loadBillingData(company.id, 'INVESTOR');
      setIsCreatePlanModalOpen(true);
    } else {
      executeCreateSpace(spaceType);
    }
  };

  const executeCreateSpace = async (spaceType: string, productId?: number) => {
    setIsLoading(true);
    try {
      if (spaceType === 'INVESTOR') {
        if (!productId) {
          toastUtils.error('플랜을 선택해주세요.');
          return;
        }
        const planResult = await billingService.createSubscription({ companyId: company.id, productId });
        if (planResult.success) {
          toastUtils.success(`${allSpaces.find(s => s.type === spaceType)?.name} 서비스가 시작되었습니다.`);
          await authService.setUserProfile();
          await refreshCompanyData();
        } else {
          toastUtils.error(planResult.message || '플랜 구독에 실패했습니다.');
        }
      } else {
        const result = await spaceService.applySpace(company.id, spaceType);
        if (result.success) {
          toastUtils.success(`${allSpaces.find(s => s.type === spaceType)?.name} 공간이 생성되었습니다.`);
          await authService.setUserProfile();
          await refreshCompanyData();
        } else {
          toastUtils.error(result.message || '공간 생성 중 오류가 발생했습니다.');
        }
      }
    } catch {
      toastUtils.error('공간 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      setPendingSpaceType(null);
    }
  };

  const openCardRegisterPopup = () => {
    const width = 700;
    const height = 850;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `/billing/card-register?companyId=${company.id}`,
      'cardRegisterPopup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      toastUtils.error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    cardPopupRef.current = popup;
  };

  // 공간 접속 핸들러
  const handleAccessSpace = (spaceType: string) => {
    const space = allSpaces.find(s => s.type === spaceType);
    if (space?.isPreparing) {
      toastUtils.info('서비스 준비 중입니다.');
      return;
    }
    
    const urls: Record<string, string> = {
      'STARTUP': `${config.startupClientUrl}/${company.code}/company-info`,
      'INVESTOR': `${config.investorClientUrl}/${company.code}/home`,
      'INNOVATE': `${config.innovateClientUrl}/${company.code}/program/list`,
    };
    
    const url = urls[spaceType];
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <PageContainer>
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection>
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroAvatarWrapper>
              <Avatar alt={company.name} src={company.logo} isUser={false} sizes="xl" />
              <HeroAvatarBadge>
                <Settings size={14} />
              </HeroAvatarBadge>
            </HeroAvatarWrapper>
            <HeroTextContent>
              <HeroCompanyCode>#{company.code}</HeroCompanyCode>
              <HeroTitle>{company.name}</HeroTitle>
              <HeroDescription>
                회사 스페이스 관리, 마스터 설정, 가입 신청을 처리하세요.
              </HeroDescription>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon $type="active">
                  <Zap size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{activeSpaces.length}</HeroStatValue>
                  <HeroStatLabel>활성 스페이스</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
              <HeroStatDivider />
              <HeroStatItem>
                <HeroStatIcon $type="available">
                  <Crown size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{masterUsers.length || '-'}</HeroStatValue>
                  <HeroStatLabel>마스터</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        {/* 기본 경로 접근 시 /space로 리다이렉트 */}
        {!validTabs.includes(lastSegment as TabType) && (
          <Navigate to={`${basePath}/space`} replace />
        )}

        {/* 탭 네비게이션 */}
        <TabNavigation>
          <TabButton 
            $active={activeTab === 'space'} 
            onClick={() => navigate(`${basePath}/space`)}
          >
            <Rocket size={18} />
            <span>스페이스 관리</span>
          </TabButton>
          <TabButton 
            $active={activeTab === 'master'} 
            onClick={() => navigate(`${basePath}/master`)}
          >
            <Crown size={18} />
            <span>마스터 설정</span>
          </TabButton>
          <TabButton 
            $active={activeTab === 'application'} 
            onClick={() => navigate(`${basePath}/application`)}
          >
            <UserCheck size={18} />
            <span>가입 신청</span>
            {pendingApplications.length > 0 && (
              <TabBadge>{pendingApplications.length}</TabBadge>
            )}
          </TabButton>
          <TabButton 
            $active={activeTab === 'billing'} 
            onClick={() => navigate(`${basePath}/billing`)}
          >
            <CreditCard size={18} />
            <span>결제 관리</span>
          </TabButton>
        </TabNavigation>

        {/* 탭 콘텐츠 */}
        <TabContent>
          {activeTab === 'space' && (
            <SpaceTab 
              activeSpaces={activeSpaces}
              availableSpaces={availableSpaces}
              companyName={company.name}
              handleAccessSpace={handleAccessSpace}
              handleCreateSpace={handleCreateSpace}
              isLoading={isLoading}
            />
          )} 

          {activeTab === 'master' && (
            <>
              <MasterTab 
                masterUsers={masterUsers}
                isLoadingUsers={isLoadingUsers}
                masterActionLoading={masterActionLoading}
                handleRemoveMaster={async (companyUserId: number) => {
                  setMasterActionLoading(companyUserId);
                  try {
                    const result = await companyUserService.removeMaster(companyUserId);
                    if (result.success) {
                      toastUtils.success('마스터 권한이 제거되었습니다.');
                      const users = await companyUserService.getCompanyUserList(company.id);
                      setCompanyUsers(users);
                      await authService.setUserProfile();
                    } else {
                      toastUtils.error(result.message || '권한 제거에 실패했습니다.');
                    }
                  } catch (error) {
                    toastUtils.error('권한 제거 중 오류가 발생했습니다.');
                  } finally {
                    setMasterActionLoading(null);
                  }
                }}
                setShowAddMasterPanel={setShowAddMasterPanel}
                showAddMasterPanel={showAddMasterPanel}
              />
              <AddMasterModal
                showAddMasterPanel={showAddMasterPanel}
                setShowAddMasterPanel={setShowAddMasterPanel}
                nonMasterUsers={nonMasterUsers}
                filteredNonMasterUsers={filteredNonMasterUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSetMaster={async (companyUserId: number) => {
                  setMasterActionLoading(companyUserId);
                  try {
                    const result = await companyUserService.setMaster(companyUserId);
                    if (result.success) {
                      toastUtils.success('마스터 권한이 부여되었습니다.');
                      const users = await companyUserService.getCompanyUserList(company.id);
                      setCompanyUsers(users);
                      await authService.setUserProfile();
                    } else {
                      toastUtils.error(result.message || '권한 부여에 실패했습니다.');
                    }
                  } catch (error) {
                    toastUtils.error('권한 부여 중 오류가 발생했습니다.');
                  } finally {
                    setMasterActionLoading(null);
                  }
                }}
                masterActionLoading={masterActionLoading}
              />
            </>
          )}

          {activeTab === 'application' && (
            <ApplicationTab 
              pendingApplications={pendingApplications}
              processedApplications={processedApplications}
              isLoadingApplications={isLoadingApplications}
              handleProcessApplication={(notificationId: string) => navigate(`/task/join-apply?n_id=${notificationId}`)}
              formatDate={(dateString: string) => {
                const date = new Date(dateString);
                return date.toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              }}
              isExpired={(expiresAt: string) => new Date(expiresAt) < new Date()}
            />
          )}

          {activeTab === 'billing' && (
            <BillingTab company={company as any} companyUser={{} as any} />
          )}
        </TabContent>

        {/* 플랜 선택 모달 (스페이스 생성 시) */}
        {isCreatePlanModalOpen && (
          <PlanSelectionModal 
            isOpen={isCreatePlanModalOpen} 
            onClose={() => {
              setIsCreatePlanModalOpen(false);
              setPendingSpaceType(null);
            }}
            companyId={company.id}
            mode="create"
            onCardRegisterRequest={() => {
              openCardRegisterPopup();
            }}
            onComplete={async () => {
              await refreshCompanyData();
            }}
          />
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
  }
`;

const HeroAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const HeroAvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: 3px solid #059669;
  border-radius: 50%;
  color: #fff;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroCompanyCode = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: rgba(255,255,255,0.2);
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: rgba(255,255,255,0.9);
  margin-bottom: 0.5rem;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
  font-variant-numeric: tabular-nums;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.8);
  max-width: 400px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroStatIcon = styled.div<{ $type: 'active' | 'available' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
`;

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroStatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`;

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.7);
`;

const HeroStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.2);
`;

// 탭 네비게이션
const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.05s both;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
    : 'transparent'};
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: ${props => props.$active ? '#fff' : semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
      : semanticColors.background.tertiary};
    color: ${props => props.$active ? '#fff' : semanticColors.text.primary};
  }
  
  svg {
    flex-shrink: 0;
  }
`;

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 100px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;

const TabContent = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
`;

export default CompanySpaceManagement;
